import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, Sparkles } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { Field, FieldDescription, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';

export type AiModelData = {
  advanced_model: string | null;
  effective_model: string;
  is_advanced_unlocked: boolean;
  unlocked_models: string[];
};

type ModelOption = {
  value: string | null;
  storedValue: string;
  labelKey: string;
  descriptionKey: string;
  isAdvanced: boolean;
};

const DEFAULT_VALUE = 'default';

const OPTIONS: ModelOption[] = [
  {
    value: null,
    storedValue: DEFAULT_VALUE,
    labelKey: 'model.options.default.label',
    descriptionKey: 'model.options.default.description',
    isAdvanced: false,
  },
  {
    value: 'gpt-5.4-2026-03-05',
    storedValue: 'gpt-5.4-2026-03-05',
    labelKey: 'model.options.gpt_5_4.label',
    descriptionKey: 'model.options.gpt_5_4.description',
    isAdvanced: true,
  },
  {
    value: 'gpt-5.5-2026-04-23',
    storedValue: 'gpt-5.5-2026-04-23',
    labelKey: 'model.options.gpt_5_5.label',
    descriptionKey: 'model.options.gpt_5_5.description',
    isAdvanced: true,
  },
];

type Props = {
  data: AiModelData;
};

export const AiModelPage = ({ data }: Props) => {
  const { t } = useTranslation(['identity', 'common']);
  const queryClient = useQueryClient();

  const mutation = useMutation<AiModelData, unknown, { advanced_model: string | null }>({
    mutationKey: [QueryKey.aiModel.patch],
    onMutate: (next) => {
      queryClient.setQueryData<AiModelData>([QueryKey.aiModel.get], (prev) =>
        prev ? { ...prev, advanced_model: next.advanced_model } : prev,
      );
    },
    onError: (_err, _vars, _context) => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.aiModel.get] });
    },
    onSuccess: (resp) => {
      queryClient.setQueryData([QueryKey.aiModel.get], resp);
    },
  });

  const isOptionLocked = (opt: ModelOption): boolean => {
    if (!opt.isAdvanced || opt.value === null) return false;
    return !data.unlocked_models.includes(opt.value);
  };

  const handleChange = (next: string) => {
    if (next === DEFAULT_VALUE) {
      mutation.mutate({ advanced_model: null });
      return;
    }
    const opt = OPTIONS.find((o) => o.storedValue === next);
    if (!opt || isOptionLocked(opt)) return;
    mutation.mutate({ advanced_model: next });
  };

  const storedOption =
    OPTIONS.find((o) => o.storedValue === (data.advanced_model ?? DEFAULT_VALUE)) ?? OPTIONS[0];
  // If the saved model is locked (e.g. a Free account that picked it before losing access), show the effective standard model.
  const currentOption = isOptionLocked(storedOption) ? OPTIONS[0] : storedOption;
  const currentValue = currentOption.storedValue;
  const anyAdvancedLocked = OPTIONS.some((o) => o.isAdvanced && isOptionLocked(o));

  return (
    <div className="max-w-2xl">
      <Field>
        <FieldLabel>{t('model.title')}</FieldLabel>
        <FieldDescription>{t('model.description')}</FieldDescription>

        <Select value={currentValue} onValueChange={handleChange}>
          <SelectTrigger className="mt-3 w-full" data-testid="ai-model-select">
            <SelectValue>
              <span className="flex items-center gap-2">
                {currentOption.isAdvanced && (
                  <Sparkles className="size-4 shrink-0 text-foreground" />
                )}
                {t(currentOption.labelKey)}
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {OPTIONS.map((opt) => {
              const locked = isOptionLocked(opt);
              return (
                <SelectItem
                  key={opt.storedValue}
                  value={opt.storedValue}
                  disabled={locked}
                  data-testid={`ai-model-option-${opt.storedValue}`}
                  className="py-2.5"
                >
                  <div className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center">
                      {opt.isAdvanced &&
                        (locked ? (
                          <Lock className="size-4 text-muted-foreground" />
                        ) : (
                          <Sparkles className="size-4 text-foreground" />
                        ))}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground">{t(opt.labelKey)}</div>
                      <div className="text-pretty text-xs text-muted-foreground">
                        {t(opt.descriptionKey)}
                      </div>
                    </div>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {anyAdvancedLocked && (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="ai-model-upgrade-cta">
            <Trans
              i18nKey="identity:model.upgrade_cta"
              t={t}
              components={{
                upgrade: (
                  <Link to="../subscription" className="text-sm text-foreground font-medium" />
                ),
              }}
            />
          </p>
        )}
      </Field>
    </div>
  );
};
